import { DestroyRef, DOCUMENT, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { AuthService, AuthState } from '@libs/shared/auth';
import { CapacitorService, EnvironmentService, MsalConfigModel } from '@libs/shared/common';
import { ConnectionAuthModel } from '@libs/shared/models';
import { firstValueFrom, switchMap, timer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppRedirectService {
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly authState: AuthState = inject(AuthState);
  private readonly router: Router = inject(Router);
  private readonly environmentService: EnvironmentService = inject(EnvironmentService);
  private readonly msalConfig: Readonly<MsalConfigModel> = this.environmentService.msalConfig();
  private readonly document: Document = inject(DOCUMENT);

  public async init(destroyRef: DestroyRef): Promise<void> {
    if (!this.capacitorService.isCapacitorApp()) {
      return;
    }

    const appUrlListener: PluginListenerHandle = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      void this.handleAppUrlOpen(event);
    });

    destroyRef.onDestroy(() => {
      void appUrlListener.remove();
    });
  }

  private async handleAppUrlOpen(event: URLOpenListenerEvent): Promise<void> {
    const auth: ConnectionAuthModel = await firstValueFrom(this.authState.getAuth());

    const url: string = event.url;
    let parsedUrl: URL | undefined;

    // Custom URL Scheme link.com.lekkerland.cep.*://cart for example
    if (url.includes(this.msalConfig.appId)) {
      const currentOrigin: string = this.document.location.origin;

      parsedUrl = new URL(url.split('://')[1], currentOrigin);
    } else if (URL.canParse(url)) {
      parsedUrl = new URL(url);
    }

    if (!parsedUrl) {
      return;
    }

    const urlToNavigateTo: string = `${parsedUrl.pathname}${parsedUrl.search}`;

    if (!auth.token) {
      await this.router.navigate(['/auth'], {
        queryParams: { returnUrl: `${parsedUrl.pathname}${parsedUrl.search}` },
      });

      // the delay is because on ios if we fire this instantly keychain has state issues.
      void firstValueFrom(timer(500).pipe(switchMap(() => this.authService.login())));
    } else {
      await this.router.navigateByUrl(urlToNavigateTo);
    }
  }
}
